import Jate from 'jates';
import { DateRange } from 'react-day-picker';
import { TIMEFRAMES } from 'src/features/analytics/components/constants';
import mixpanel from 'src/shared/lib/mixpanel';
import { AFTER_TS_PARAM, BEFORE_TS_PARAM, LIVE_PARAM } from 'src/shared/constants/urlParams.constant';
import { ToDateAble } from 'src/shared/types/util.types';

export function minutesToWords(minutes: number) {
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else if (minutes < 1440) {
    const hours = Math.floor(minutes / 60);
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  } else {
    const days = Math.floor(minutes / 1440);
    return `${days} day${days !== 1 ? 's' : ''}`;
  }
}

/**
 * Format remaining minutes for backfill progress display
 * Rounds to nearest 5 minutes for cleaner UI
 * @param minutes - The number of remaining minutes
 * @returns Formatted string like "less than 5 minutes", "30 minutes", "2 hours", or "2h 30m"
 */
export function formatBackfillRemainingTime(minutes: number | null): string {
  if (!minutes || minutes <= 0) return '';

  // Round to nearest 5 minutes for cleaner display
  const rounded = Math.round(minutes / 5) * 5;

  if (rounded < 5) return 'less than 5 minutes';
  if (rounded < 60) return `${rounded} minutes`;

  const hours = Math.floor(rounded / 60);
  const mins = rounded % 60;

  if (mins === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
  return `${hours}h ${mins}m`;
}

export function convertToDate(date: ToDateAble) {
  if (!date) {
    return undefined;
  }
  if (typeof date === 'number') {
    const currentTimeMs = Date.now();
    const currentTimeSeconds = Math.floor(currentTimeMs / 1000);

    // date is in milliseconds
    if (date > currentTimeSeconds + 24 * 60 * 60) {
      return new Date(date);
    }
    // date is in seconds, convert to milliseconds
    return new Date(date * 1000);
  }
  return new Date(date);
}

export function getIntervalInDays(beforeTs: string | number, afterTs: string | number) {
  const startDate = new Date(Number(afterTs) * 1000);
  startDate.setHours(12, 0, 0, 0);
  const endDate = new Date(Number(beforeTs) * 1000);
  endDate.setHours(12, 0, 0, 0);

  if (startDate === endDate) {
    return 1;
  }

  let days = 0;
  for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
    days++;
  }
  return days;
}

export function getWeekNumber(date: Date) {
  const newDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  newDate.setUTCDate(newDate.getUTCDate() + 4 - (newDate.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(newDate.getUTCFullYear(), 0, 1));
  const weekNumber = ((newDate.getTime() - yearStart.getDate()) / 86400000 + 1) / 7;
  return Math.ceil(weekNumber);
}

export function getStartOfWeek(date: Date) {
  const day = date.getDay(); // Get the day of the week (0-6, where 0 is Sunday)
  const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust if the day is Sunday
  return new Date(date.setDate(diff)); // Set the date to the start of the week
}

export function areDatesInSameWeek(date1: Date, date2: Date) {
  const startOfWeek1 = getStartOfWeek(new Date(date1));
  const startOfWeek2 = getStartOfWeek(new Date(date2));

  // Compare the start of the week dates
  return startOfWeek1.toDateString() === startOfWeek2.toDateString();
}

export function getAfterTimestamp(interval: number) {
  const now = new Date();
  now.setHours(12, 0, 0, 0); // Set to mid of day, to avoid issues with timezone drift
  const intervalInMillis = (interval - 1) * 24 * 60 * 60 * 1000;
  const pastDate = new Date(now.getTime() - intervalInMillis);
  pastDate.setHours(12, 0, 0, 0);
  return Math.floor(pastDate.getTime() / 1000);
}

export function getBeforeTimestamp() {
  const now = new Date();
  now.setHours(23, 59, 59); // Set to end of day
  return Math.floor(now.getTime() / 1000);
}

export function handleLiveTimeframe(interval: number, updateUrl: boolean = true) {
  const afterTimestamp = getAfterTimestamp(interval);
  const beforeTimestamp = getBeforeTimestamp();

  const params = new URLSearchParams(window.location.search);
  params.set(LIVE_PARAM, '1');
  params.set(AFTER_TS_PARAM, afterTimestamp.toString());
  params.set(BEFORE_TS_PARAM, beforeTimestamp.toString());
  if (updateUrl) {
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  }
  const timeframeInterval = String(interval);
  const timeframe =
    timeframeInterval in TIMEFRAMES ? TIMEFRAMES[timeframeInterval as keyof typeof TIMEFRAMES] : undefined;
  mixpanel.track('Timeframe Changed', {
    timeframe,
    origin: window.location.pathname.split('/')[1],
    custom: false,
  });
  return {
    after_ts: afterTimestamp,
    before_ts: beforeTimestamp,
  };
}

export function applyCustomRange(customTimeRange: DateRange) {
  if (!customTimeRange.from || !customTimeRange.to) {
    throw new Error('Custom time range is required');
  }
  const params = new URLSearchParams(window.location.search);
  params.delete(LIVE_PARAM);
  params.set(AFTER_TS_PARAM, String(customTimeRange.from.getTime() / 1000));
  params.set(BEFORE_TS_PARAM, String(customTimeRange.to.getTime() / 1000));
  window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
}

export function getTimeframeLabel(beforeTs: string | number, afterTs: string | number, customTimeRange?: DateRange) {
  const interval = getIntervalInDays(beforeTs, afterTs);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const beforeDate = new Date(Number(beforeTs) * 1000);
  beforeDate.setHours(0, 0, 0, 0);

  const timeframeInterval = String(interval);
  const timeframe =
    timeframeInterval in TIMEFRAMES ? TIMEFRAMES[timeframeInterval as keyof typeof TIMEFRAMES] : undefined;
  if (beforeDate.getTime() === today.getTime()) {
    return 'Last ' + timeframe;
  }

  return `${customTimeRange?.from?.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })} - ${customTimeRange?.to?.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })}`;
}

export function isToday(date: ToDateAble) {
  if (!date) {
    return false;
  }
  const convertedDate = convertToDate(date);
  if (!convertedDate) {
    return false;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return convertedDate.toDateString() === today.toDateString();
}

export function getEmailTimestampDisplay(timestamp: ToDateAble) {
  if (!timestamp) {
    throw new Error('Timestamp is required');
  }
  const date = convertToDate(timestamp)!;
  const today = new Date();
  const isToday = today.toDateString() === date.toDateString();
  const isYesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000).toDateString() === date.toDateString();
  return isToday ? `${new Jate(date).format('HH:mm')}` : isYesterday ? 'Yesterday' : new Jate(date).format('d MMM yy');
}

export function getDateDisplay(date: ToDateAble, includeTime: boolean = true) {
  if (!date) {
    return 'N/A';
  }
  const convertedDate = convertToDate(date);
  if (!convertedDate) {
    return 'N/A';
  }
  return new Jate(convertedDate).format(
    new Date().getFullYear() === convertedDate.getFullYear()
      ? includeTime
        ? 'MMM d - HH:mm'
        : 'MMM d'
      : includeTime
        ? 'MMM d, yyyy - HH:mm'
        : 'MMM d, yyyy'
  );
}
